import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { doc, getDoc } from 'firebase/firestore';
import { FaArrowLeft, FaArrowRight, FaRegClock } from 'react-icons/fa';
import { db } from '../../firebase/config';
import SEO from '../../components/SEO';
import ApplicationWizard from './ApplicationWizard';
import JobMeta from './JobMeta';
import { CONTRACT_TYPES, jobTitle } from './careersOptions';
import { descriptionHtml, descriptionText } from './richText';
import './Careers.css';

// schema.org JobPosting, so the offer can appear in Google's job search
const jobPostingSchema = (job, url) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: jobTitle(job),
    description: descriptionHtml(job),
    datePosted: (job.publishedAt || job.createdAt || new Date().toISOString()).slice(0, 10),
    employmentType: (CONTRACT_TYPES.find(c => c.value === job.contractType) || {}).schema || 'OTHER',
    hiringOrganization: {
      '@type': 'Organization',
      name: 'OPTEAM Services',
      sameAs: 'https://www.opteam-services.com',
      logo: 'https://www.opteam-services.com/logo192.png'
    },
    jobLocation: {
      '@type': 'Place',
      address: { '@type': 'PostalAddress', addressLocality: job.location || 'Meudon', addressCountry: 'FR' }
    },
    url,
    directApply: true
  };
  if ((job.workplace || job.remote) === 'remote') {
    schema.jobLocationType = 'TELECOMMUTE';
    schema.applicantLocationRequirements = { '@type': 'Country', name: 'France' };
  }
  return schema;
};

const JobDetail = () => {
  const { jobId } = useParams();
  const { t } = useTranslation();
  const [job, setJob] = useState(null);
  const [status, setStatus] = useState('loading');
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const snap = await getDoc(doc(db, 'jobs', jobId));
        if (snap.exists() && snap.data().published) {
          setJob({ id: snap.id, ...snap.data() });
          setStatus('ready');
        } else {
          setStatus('missing');
        }
      } catch (error) {
        // Unpublished offers are not readable by visitors (permission denied)
        console.error('Error fetching job:', error);
        setStatus('missing');
      }
    };
    fetchJob();
  }, [jobId]);

  const canonical = `/careers/${jobId}`;
  // Contact, CV, one screen per question, final screen: ~30 s each
  const stepCount = job ? 3 + (job.screeningQuestions || []).length : 0;
  const minutes = Math.max(2, Math.round(stepCount / 2));

  const applyButton = (
    <button type="button" className="btn btn-primary job-apply-btn" onClick={() => setApplying(true)}>
      {t('careers.applyCta')} <FaArrowRight />
    </button>
  );

  return (
    <div className="careers-page job-page">
      {status === 'ready' ? (
        <>
          <SEO title={jobTitle(job)} description={descriptionText(job).slice(0, 155)} canonical={canonical} />
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify(jobPostingSchema(job, `https://www.opteam-services.com${canonical}`))}
            </script>
          </Helmet>
        </>
      ) : (
        <SEO title={t('careers.jobsTitle')} canonical={canonical} noindex={status === 'missing'} />
      )}

      <section className="careers-hero job-hero">
        <div className="container">
          <Link to="/careers" className="job-back"><FaArrowLeft /> {t('careers.backToJobs')}</Link>
          {status === 'loading' && <p>{t('careers.loading')}</p>}
          {status !== 'loading' && (
            <motion.div
              key={status}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {status === 'ready' && (
                <>
                  <h1>{jobTitle(job)}</h1>
                  <JobMeta job={job} />
                  <div className="job-hero-cta">{applyButton}</div>
                </>
              )}
              {status === 'missing' && (
                <>
                  <h1>{t('careers.jobMissingTitle')}</h1>
                  <p>{t('careers.jobMissingText')}</p>
                </>
              )}
            </motion.div>
          )}
        </div>
      </section>

      {status === 'ready' && (
        <section className="job-body">
          <div className="container job-layout">
            <div
              className="job-description job-description-content"
              lang={job.language || 'fr'}
              dangerouslySetInnerHTML={{ __html: descriptionHtml(job) }}
            />
            <aside className="job-apply-card">
              <h2>{t('careers.applyCardTitle')}</h2>
              <JobMeta job={job} />
              <p className="job-apply-time">
                <FaRegClock /> {t('careers.applyCardText', { steps: stepCount, minutes })}
              </p>
              {applyButton}
            </aside>
          </div>
        </section>
      )}

      {status === 'missing' && (
        <section className="job-body">
          <div className="container job-missing-actions">
            <Link to="/careers" className="btn btn-primary">{t('careers.seeAllJobs')}</Link>
          </div>
        </section>
      )}

      {applying && <ApplicationWizard job={job} onClose={() => setApplying(false)} />}
    </div>
  );
};

export default JobDetail;
